import { useRef, useState } from 'react';

import type { CarouselRef, SimpleSliderRef } from '@capsule/consumer-design-system';
import { mediaQuerySizes, Modal, SimpleSlider, XIconButton } from '@capsule/consumer-design-system';

// eslint-disable-next-line import/no-unresolved
import styled, { css } from 'styled-components';

export const Image = styled.img`
  object-fit: contain;
  height: 100%;
  width: 100%;
`;

export const Divider = styled.hr`
  border: none; /* Remove default border */
  height: 1px; /* Set thickness */
  background-color: ${({ theme }) => theme.colors.capsuleGreen10}; /* Set color */
  margin: 24px 0; /* Add spacing */
`;

interface ThumbnailProps {
  hasBorder?: boolean;
}
export const Thumbnail = styled.img<ThumbnailProps>`
  object-fit: contain;
  justify-content: center;
  overflow: auto;
  height: 96px;
  width: 96px;

  border: ${({ theme, hasBorder }) => (hasBorder ? `3px solid ${theme.colors.capsuleGreen60}` : 'none')};

  ${props => props.theme.mediaQueries.small} {
    width: 96px;
  }
`;

const Header = styled.div`
  justify-content: space-between;
  padding: 24px;
`;

const DialogCloseButton = styled(XIconButton)`
  color: ${({ theme }) => theme.colors.capsuleGray50};
  height: 24px;
  margin-left: auto;
  cursor: pointer;
`;

interface EssentialsItemImageGalleryModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  imageURLs: string[];
}

export const GalleryModal = ({ isOpen, setIsOpen, imageURLs }: EssentialsItemImageGalleryModalProps) => {
  const carouselRef = useRef<CarouselRef>(null);
  const sliderRef = useRef<SimpleSliderRef>(null);
  const [curImageIndex, setCurImageIndex] = useState(0);
  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={isOpen => {
        setIsOpen(isOpen);
      }}
      isBodyPaddingDisabled={true}
      isHeaderDisabled={true} // Disable the header to allow for fullscreen content in this modal.
    >
      <div
        css={css`
          height: 100vh;
          display: flex;
          flex-direction: column;
        `}
      >
        <Header>
          <DialogCloseButton onClick={() => setIsOpen(false)} />
        </Header>
        <div
          css={css`
            overflow: auto;
            height: 100%;
            width: 100%;
            padding: ${({ theme }) => theme.space.s3};
          `}
        >
          {imageURLs.length > 0 && (
            <SimpleSlider
              ref={sliderRef}
              slides={imageURLs.map((url, index) => (
                <Image src={url} key={index} />
              ))}
              isLooped={true}
              slidesPerView={1}
              slidesPerGroup={1}
              isPaginationCircleVisible={false}
              isNavigationButtonVisible={false}
              onPrevPageCallback={(curIndex: number) => {
                setCurImageIndex(curIndex);
              }}
              onNextPageCallback={(curIndex: number) => {
                setCurImageIndex(curIndex);
              }}
              breakpoints={{
                [mediaQuerySizes.medium]: {
                  isNavigationButtonVisible: true,
                },
              }}
            />
          )}
        </div>
        <Divider />
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100px;
            overflow-x: scroll;
            margin: 0 24px 24px 24px;
            gap: 12px;
            white-space: nowrap;
            flex-shrink: 0;
          `}
        >
          {imageURLs.map((url: string, index: number) => (
            <Thumbnail
              alt=""
              src={url}
              key={index}
              hasBorder={index === curImageIndex}
              onClick={() => {
                carouselRef.current?.goToIndex(index);
                setCurImageIndex(index);
                sliderRef.current?.navigateToPage(index);
              }}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};
